import { useState } from 'react';
import PropTypes from 'prop-types';

import getGqlClient from '../../clients/graphql/tenant';
import { uploadFileToS3Bucket } from '../../api/amazon-s3';
import { getDocumentUploadUrl } from '../../queries/graphql/document';

import { FILES_MAX_SIZE_BYTES } from '../../constants';

import FileUploader from '../FileUploader/FileUploader';

const FileUploaderMulti = ({
  id: idBase,
  title = '',
  className = '',
  required = false,
  dataTest: dataTestBase = 'file-uploader-multi',
  maxFiles = 10,
  onFileUploaded,
  onFileDeleted,
  onFileUploadError,
  maxFileSizeBytes = FILES_MAX_SIZE_BYTES
}) => {
  const [files, setFiles] = useState([]);

  const uploadFile = async file => {
    try {
      const response = await getDocumentUploadUrl(getGqlClient())(file.type);
      const dataUpload = response.data.documentUploadUrl;
      const { signedUploadUrl, filePath } = dataUpload;

      await uploadFileToS3Bucket(signedUploadUrl, file);

      return { filePath };
    } catch (error) {
      if (typeof onFileUploadError === 'function') {
        onFileUploadError(error);
      }

      return null;
    }
  };

  const handleFileUploaded = ({ id, filePath }) => {
    const updatedFiles = [
      ...files.filter(file => file.documentType !== id),
      { documentType: id, filePath }
    ];
    setFiles(updatedFiles);

    if (typeof onFileUploaded === 'function') {
      onFileUploaded({ id, filePath, files: updatedFiles });
    }
  };

  const handleDeleteFile = id => {
    const fileToDelete = files.find(file => file.documentType === id);
    const updatedFiles = files.filter(file => file.documentType !== id);
    setFiles(updatedFiles);

    if (typeof onFileDeleted === 'function') {
      onFileDeleted({
        id,
        filePath: fileToDelete.filePath,
        files: updatedFiles
      });
    }
  };

  const allFileIds = Array.from(
    { length: maxFiles },
    (_, index) => `${idBase}_${index + 1}`
  );
  const hasFileForId = id => files.some(file => file.documentType === id);
  const fileIds = allFileIds.filter(hasFileForId);
  const firstEmptyFileId = allFileIds.find(id => !hasFileForId(id));
  const visibleFileIds =
    fileIds.length < maxFiles && firstEmptyFileId
      ? [...fileIds, firstEmptyFileId]
      : fileIds;
  const preloaded = hasFileForId(visibleFileIds);

  return visibleFileIds.map(id => (
    <FileUploader
      key={id}
      className={className}
      title={title}
      id={id}
      uploadFile={uploadFile}
      required={required}
      onDelete={handleDeleteFile}
      onFileUploaded={handleFileUploaded}
      preloaded={preloaded}
      dataTest={`${dataTestBase}-${id}`}
      maxFileSizeBytes={maxFileSizeBytes}
    />
  ));
};

FileUploaderMulti.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  className: PropTypes.string,
  required: PropTypes.bool,
  dataTest: PropTypes.string,
  maxFiles: PropTypes.number,
  onFileUploaded: PropTypes.func,
  onFileDeleted: PropTypes.func,
  maxFileSizeBytes: PropTypes.number
};

export default FileUploaderMulti;
